// OrlDnaDirectUpload — no-wizard direct handler for a MetaHuman `head.dna`.
//
// Dropping a .dna on the content browser converts it to the packed ORL rig in
// the browser (riglogic wasm, off-thread — see lib/orl/dnaToPackWorker.js) and
// uploads the result to this character's /ogs collection under the bundle's own
// asset name. No form: the pack has no options, only an identity.
//
// Mirrors EnvironmentDirectUpload — fires on mount, closes itself on success,
// and on failure stays up with the message and the stage it died in so the user
// dismisses it deliberately (loud failure per CLAUDE.md UX rules).
//
// Sequencing: converting is the slow half (tens of seconds on a 39 MB DNA) and
// reports the wasm's own log lines; uploading is the half with real byte
// progress. Both are shown, because a bar that sits at "0%" for 40 s and then
// races to 100% reads as a hang.

import React, { useEffect, useRef, useState } from 'react'
import WizardShell from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { convertAndUploadDna, orlPackUploadName } from '../../../lib/orl/dnaUpload'
import { convertDnaInWorker } from '../../../lib/orl/dnaToPackWorker'

function fmtSize(bytes) {
  if (bytes == null || bytes === 0) return '—'
  const units = ['B', 'KB', 'MB', 'GB']
  let n = Number(bytes), i = 0
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 ? 0 : 1)} ${units[i]}`
}

const TITLES = {
  reading: 'Reading .dna…',
  converting: 'Converting rig…',
  uploading: 'Uploading rig…',
  done: 'Rig uploaded',
}

export default function OrlDnaDirectUpload({ file, onDone, onCancel }) {
  const [stage, setStage] = useState('reading')
  const [note, setNote] = useState('')
  const [progress, setProgress] = useState({ fraction: 0, loaded: 0, total: 0 })
  const [error, setError] = useState(null)
  const fired = useRef(false)

  useEffect(() => {
    if (!file || fired.current) return
    fired.current = true
    convertAndUploadDna(file, {
      readBytes: (f) => f.arrayBuffer(),
      convert: (bytes, { onLog }) => convertDnaInWorker(bytes, { onLog }),
      upload: (packFile, { onProgress }) => CustomAssetManager.uploadOgsAsset(packFile, { onProgress }),
      onStage: (s, detail = {}) => {
        setStage(s)
        if (detail.message) setNote(detail.message)
        if (detail.progress) setProgress(detail.progress)
        if (s === 'converting' && !detail.message) setNote(`${fmtSize(detail.bytes)} of DNA — this runs off the UI thread`)
      },
    })
      .then(() => onDone?.())
      .catch(e => setError(`${e?.stage ? `[${e.stage}] ` : ''}${e?.message ?? String(e)}`))
  }, [file, onDone])

  const pct = stage === 'uploading' && progress.fraction >= 0 ? Math.round(progress.fraction * 100) : null
  const indeterminate = stage === 'reading' || stage === 'converting'

  return (
    <WizardShell
      title={error ? 'Rig conversion failed' : (TITLES[stage] ?? 'Working…')}
      subtitle={file?.name}
      onCancel={onCancel}
      onSubmit={onCancel}
      submitDisabled={!error}
      submitLabel={error ? 'Close' : (pct != null ? `${pct}%` : 'Working…')}
      error={error}
    >
      {!error && (
        <div style={{ padding: '8px 0 4px' }}>
          <div style={{
            width: '100%', height: 10,
            background: 'var(--m-bg-graph)', border: '1px solid var(--m-line-hi)',
            borderRadius: 4, overflow: 'hidden', position: 'relative',
          }}>
            {indeterminate ? (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(90deg, transparent 0%, var(--m-accent) 50%, transparent 100%)',
                backgroundSize: '40% 100%',
                animation: 'aos-dna-stripe 1.2s linear infinite',
              }} />
            ) : (
              <div style={{
                width: `${Math.max(2, (progress.fraction || 0) * 100)}%`, height: '100%',
                background: 'var(--m-accent)',
                transition: 'width 0.15s linear',
              }} />
            )}
          </div>

          <div style={{
            marginTop: 8, color: 'var(--m-fg-2)', fontSize: 11,
            display: 'flex', justifyContent: 'space-between', gap: 8,
          }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {stage === 'uploading' ? `${orlPackUploadName()} → /ogs` : (note || TITLES[stage])}
            </span>
            <span>
              {stage === 'uploading' && progress.total > 0
                ? `${fmtSize(progress.loaded)} / ${fmtSize(progress.total)}`
                : fmtSize(file?.size)}
            </span>
          </div>

          <div style={{ marginTop: 10, color: 'var(--m-fg-3)', fontSize: 10 }}>
            Converting a MetaHuman DNA takes a while — please don't close this tab.
          </div>
        </div>
      )}
      <style>{`
        @keyframes aos-dna-stripe { from { background-position: -40% 0; } to { background-position: 140% 0; } }
      `}</style>
    </WizardShell>
  )
}
